import {getUrlParams} from '@rambler-id/url'
import {
  BaseObject,
  type Size,
  type PlayerError,
  type Core,
  ErrorMixin,
  Utils
} from '@clappr/core'
import type {Options} from 'common/types/options'
import {
  AdvertisementProvider,
  type AdvertisementBlockType,
  type AdvertisementSource,
  type AdvertisementType
} from 'common/api/records'
import {BrowserAdInfo} from '../../browser-ad-info'
import {GPMD_MIN_WIDTH} from '../vast-playback/ad-systems'

export interface AdPlaybackConfig {
  containerElement: HTMLElement
  videoElement: HTMLVideoElement
  clickTrackingElement?: HTMLElement
  muted?: boolean
}

export interface AdSourceOptions {
  referrer: string
  width: number
  blockType: AdvertisementBlockType
}

export abstract class AdPlayback<S> extends BaseObject<AdPlaybackConfig> {
  /* eslint-disable @typescript-eslint/no-empty-function */
  public static async preload(_: Options): Promise<void> {}
  public static destroy(): void {}
  /* eslint-enable @typescript-eslint/no-empty-function */

  public static canPlay(_source: AdvertisementSource): boolean {
    return true
  }

  private static MAX_REFERRER_LENGTH = 250

  public static getAdPlaybackSources(
    source: AdvertisementSource,
    options: AdSourceOptions
  ): AdvertisementSource {
    const {referrer = '', width} = options

    if (source.provider === AdvertisementProvider.VAST && source.url) {
      const now = new Date()

      const query = referrer ? getUrlParams(referrer) : {}

      const encodedReferrer = encodeURIComponent(
        referrer.substring(0, AdPlayback.MAX_REFERRER_LENGTH)
      )

      const minWidth = source.url.match(/(ssp\.rambler\.ru|ad\.mail)/)
        ? GPMD_MIN_WIDTH
        : 0

      source.url = source.url
        .replace(/\[pr\]/g, Utils.uniqueId(''))
        .replace(/\[random\]/g, Utils.uniqueId(''))
        .replace(/\[date\]/g, now.getDate().toString())
        .replace(/\[day\]/g, now.getDay().toString())
        .replace(/\[hours\]/g, now.getHours().toString())
        .replace(/\[device\]/g, BrowserAdInfo.getDeviceName())
        .replace(/\[all_device\]/g, BrowserAdInfo.getDeviceType())
        .replace(/\[gpmd_browser\]/g, BrowserAdInfo.getBrowserName())
        .replace(
          /\[dimensions\]/g,
          BrowserAdInfo.getScreenSize({width, minWidth})
        )
        .replace(/\[referer\]/g, encodedReferrer)
        .replace(/\[fullscreen\]/g, '1')
        // transform [arg_name] in adTagUrl to [name=value] from referrer query
        .replace(/\[arg_(.*?)\]/gi, (_, name) =>
          query[name] ? `${name}=${query[name]}` : ''
        )
        // transform [var_name] in adTagUrl to value from referrer query
        .replace(/\[var_(.*?)\]/gi, (_, name) =>
          query[name] ? `${query[name]}` : ''
        )
    }

    return source
  }

  public abstract name: string
  public abstract allowBlock?: boolean
  public abstract reloadFailedAdTag?: boolean

  public sources!: S
  protected core: Core<Options>
  protected createError = ErrorMixin.createError.bind(this)

  protected get playerError(): PlayerError<Options> | undefined {
    return this.core.playerError
  }

  public constructor(options: AdPlaybackConfig, core: Core<Options>) {
    super(options)
    this.core = core
  }

  public abstract load(sources: S): Promise<void>
  public abstract start(): void
  public abstract pause(): void
  public abstract resume(): void
  public abstract skip(): void
  public abstract getVolume(): number
  public abstract setVolume(volume: number): void
  public abstract resize(size: Size): void
  public abstract getDuration(): number
  public abstract getCurrentTime(): number
  public abstract isLinear(): boolean
  public abstract getAdSystem(): string
  public abstract getAdType(): AdvertisementType
  public abstract destroy(): void
}
